import type { TravelMode } from '@/src/lib/hooks/neighborhood';

export type PetType = 'cats' | 'dogs';

export type LaundryType = 'inUnit' | 'shared';

export type AmenityType =
  | 'cooling'
  | 'heating'
  | 'parking'
  | 'gatedEntry'
  | 'doorman'
  | 'fitnessCenter'
  | 'swimmingPool'
  | 'dishwasher';

export type PropertyType = 'apartment' | 'condo' | 'duplex' | 'house' | 'townhouse';

export type CreatedWithinType = 'hour' | 'day' | 'week' | 'month';

export type RentalType = 'rental' | 'room' | 'sublet' | 'corporate';

export type RestrictionType = 'income' | 'senior' | 'student' | 'military' | 'none';

export type AdditionalOptType =
  | 'requiresPhotos'
  | 'requiresPrice'
  | 'forRentByOwner'
  | 'hasOffers'
  | 'acceptingApplications';

/** The shared commute TravelMode vocabulary (Google Maps naming). */
export type CommuteTravelMode = TravelMode;

export type CommuteTimeMode = 'now' | 'rushHour' | 'offPeak';

export type CommuteMinutes = 15 | 30 | 45 | 60;

export type CommuteFilter = {
  lat: number;
  lon: number;
  /** Human-readable destination label; null when restored from a legacy link without one. */
  name: string | null;
  mode: CommuteTravelMode;
  timeMode: CommuteTimeMode;
  minutes: CommuteMinutes;
};

export type FilterState = {
  price: { min: number | null; max: number | null };
  bedrooms: { min: number | null; max: number | null };
  bathrooms: { min: number | null };
  pets: Array<PetType>;
  laundry: Array<LaundryType>;
  amenities: Array<AmenityType>;
  furnished: boolean;
  propertyTypes: Array<PropertyType>;
  sqft: { min: number | null; max: number | null };
  createdWithin: CreatedWithinType | null;
  rentalTypes: Array<RentalType>;
  restrictions: RestrictionType | null;
  moveInDate: { start: string | null; end: string | null };
  keywords: string;
  additionalOpts: Array<AdditionalOptType>;
  /** All-or-nothing: null until destination + max minutes are both chosen. */
  commute: CommuteFilter | null;
  orderBy: string | null;
};

export const DEFAULT_FILTERS: FilterState = {
  price: { min: null, max: null },
  bedrooms: { min: null, max: null },
  bathrooms: { min: null },
  pets: [],
  laundry: [],
  amenities: [],
  furnished: false,
  propertyTypes: [],
  sqft: { min: null, max: null },
  createdWithin: null,
  rentalTypes: [],
  restrictions: null,
  moveInDate: { start: null, end: null },
  keywords: '',
  additionalOpts: [],
  commute: null,
  orderBy: null,
};

export const FILTER_URL_KEYS = [
  'price',
  'beds',
  'baths',
  'pets',
  'laundry',
  'amenities',
  'furnished',
  'propertyTypes',
  'sqft',
  'createdWithin',
  'rentalTypes',
  'restrictions',
  'avail',
  'keywords',
  'additionalOpts',
  // Legacy commute param names (scalar values despite the plural spelling)
  // so shared links and saved-search emails from hotpads-web keep working,
  // plus commuteName to carry the destination label across share/reload.
  'commuteLats',
  'commuteLons',
  'commuteMode',
  'commuteTime',
  'commuteTimeMode',
  'commuteName',
  'orderBy',
] as const;

export const VALID_PET_VALUES = new Set<string>(['cats', 'dogs']);
export const VALID_LAUNDRY_VALUES = new Set<string>(['inUnit', 'shared']);
export const VALID_AMENITY_VALUES = new Set<string>([
  'cooling',
  'heating',
  'parking',
  'gatedEntry',
  'doorman',
  'fitnessCenter',
  'swimmingPool',
  'dishwasher',
]);
export const VALID_PROPERTY_TYPE_VALUES = new Set<string>(['apartment', 'condo', 'duplex', 'house', 'townhouse']);
export const VALID_CREATED_WITHIN_VALUES = new Set<string>(['hour', 'day', 'week', 'month']);
export const VALID_RENTAL_TYPE_VALUES = new Set<string>(['rental', 'room', 'sublet', 'corporate']);
export const VALID_RESTRICTION_VALUES = new Set<string>(['income', 'senior', 'student', 'military', 'none']);
export const VALID_ADDITIONAL_OPT_VALUES = new Set<string>([
  'requiresPhotos',
  'requiresPrice',
  'forRentByOwner',
  'hasOffers',
  'acceptingApplications',
]);
export const VALID_ORDER_BY_VALUES = new Set<string>([
  // The renter's explicit "Recommended". Distinct from an absent orderBy, which defers to the
  // BE slug default.
  'score',
  'activated',
  'weekViews',
  'highPrice',
  'lowPrice',
]);
